import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllMolecules,
  getMoleculeSVG,
  getMoleculeRender,
  getUserOrganizations,
} from "../../utils/Api";
import { Atom } from "lucide-react";
import DocumentSelect from "../../components/DocumentSelect";
import SmilesExportButton from "../../components/SmilesExportButton";
import { SmilesExportFilters } from "../../hooks/useSmilesExport";
import { slugify } from "../../utils/slug";

interface MoleculeItem {
  smiles: string;
  graph?: { symbols: string[]; coords: number[][]; edges: number[][] } | null;
  bbox_width?: number | null;
  bbox_height?: number | null;
  document_id: string;
  document_name: string;
  page_id: string;
  source: "personal" | "organisation" | "shared";
  organisation_name: string;
}

interface OrgInfo {
  id: string;
  name: string;
  role: string;
}

type TabKey = "all" | "personal" | "shared" | string; // string = org id

const PAGE_SIZE = 48;

const MoleculeCard: React.FC<{ molecule: MoleculeItem }> = ({ molecule }) => {
  const navigate = useNavigate();
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);
  const [inView, setInView] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "150px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    let objectUrl = "";

    const load = async () => {
      try {
        if (molecule.graph?.symbols?.length) {
          const renderParams: Parameters<typeof getMoleculeRender>[0] = {
            symbols: molecule.graph.symbols,
            coords: molecule.graph.coords,
            edges: molecule.graph.edges,
            force_aspect: true,
          };
          if (molecule.bbox_width)
            renderParams.bbox_width = molecule.bbox_width;
          if (molecule.bbox_height)
            renderParams.bbox_height = molecule.bbox_height;
          const res = await getMoleculeRender(renderParams);
          if (cancelled) return;
          const url = URL.createObjectURL(res.data as Blob);
          objectUrl = url;
          setImgUrl(url);
        } else if (molecule.smiles) {
          const res = await getMoleculeSVG(molecule.smiles);
          if (cancelled) return;
          const blob = new Blob([res.data], { type: "image/svg+xml" });
          const url = URL.createObjectURL(blob);
          objectUrl = url;
          setImgUrl(url);
        } else {
          setImgError(true);
        }
      } catch {
        if (!cancelled) setImgError(true);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [inView, molecule.smiles, molecule.graph]);

  return (
    <div
      ref={cardRef}
      className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-[#1d365d]/40 hover:shadow-md transition-all duration-150 flex flex-col cursor-pointer"
      onClick={() => navigate(`/view/${molecule.document_id}`)}
    >
      {/* Structure */}
      <div className="h-[160px] bg-white flex items-center justify-center p-4 overflow-hidden">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt="molecule"
            className="max-w-full max-h-full object-contain"
          />
        ) : imgError ? (
          <div className="flex flex-col items-center gap-1 text-gray-300">
            <Atom size={32} />
            <span className="text-xs">No preview</span>
          </div>
        ) : (
          <div className="w-full h-[120px] bg-gray-100 animate-pulse rounded-lg" />
        )}
      </div>

      {/* Document name */}
      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/60">
        <p
          className="text-xs text-[#1d365d]/70 truncate text-center"
          title={molecule.document_name}
        >
          {molecule.document_name}
        </p>
      </div>
    </div>
  );
};

const Molecules: React.FC = () => {
  const [molecules, setMolecules] = useState<MoleculeItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    getUserOrganizations().then((res) => {
      if (cancelled) return;
      setOrgs(res.data.organizations || []);
    });
    return () => { cancelled = true; };
  }, []);

  const fetchMolecules = useCallback(
    async (offset: number, append: boolean, docId: string, tab: TabKey) => {
      try {
        if (offset === 0) setLoading(true);
        else setLoadingMore(true);

        const params: Parameters<typeof getAllMolecules>[0] = {
          limit: PAGE_SIZE,
          offset,
        };
        if (docId) params.document_id = docId;

        if (tab === "personal") {
          params.source = "personal";
        } else if (tab !== "all") {
          // tab is an org id
          params.organisation_id = tab;
        }

        const { data } = await getAllMolecules(params);

        if (append) {
          setMolecules((prev) => [...prev, ...data.molecules]);
        } else {
          setMolecules(data.molecules);
        }
        setTotal(data.total);
        setError(null);
      } catch (err: any) {
        setError(err.message ?? "Failed to load molecules");
        if (!append) setMolecules([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchMolecules(0, false, selectedDoc?.id ?? "", activeTab);
  }, [fetchMolecules, selectedDoc, activeTab]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: "all", label: "All" },
    { key: "personal", label: "My Documents" },
    ...orgs.map((org: OrgInfo) => ({ key: org.id, label: org.name })),
  ];

  const remaining = total - molecules.length;

  const exportFilters: SmilesExportFilters = {};
  if (selectedDoc?.id) exportFilters.document_id = selectedDoc.id;
  if (activeTab === "personal") {
    exportFilters.source = "personal";
  } else if (activeTab !== "all") {
    exportFilters.organisation_id = activeTab;
  }

  const exportScope = selectedDoc
    ? `doc-${slugify(selectedDoc.name)}`
    : activeTab === "all"
      ? "all"
      : activeTab === "personal"
        ? "personal"
        : `org-${slugify(orgs.find((o) => o.id === activeTab)?.name ?? activeTab)}`;

  useEffect(() => {
    if (remaining <= 0 || loadingMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchMolecules(molecules.length, true, selectedDoc?.id ?? "", activeTab);
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [remaining, loadingMore, molecules.length, fetchMolecules, selectedDoc, activeTab]);

  return (
    <div className="bg-white h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-8 sm:pt-12 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">
              Molecules
            </h3>
            <p className="text-base text-gray-500 mt-0.5">
              {loading
                ? "Loading molecules…"
                : `${total.toLocaleString()} unique molecule${total !== 1 ? "s" : ""} identified across all documents`}
            </p>
          </div>

          <SmilesExportButton
            mode="molecules"
            filters={exportFilters}
            scopeSlug={exportScope}
            disabled={total === 0}
          />
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 mb-5 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setSelectedDoc(null);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors duration-100 border-b-2 -mb-px ${
                activeTab === tab.key
                  ? "border-[#1d365d] text-[#1d365d]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mb-6">
          <DocumentSelect value={selectedDoc} onChange={setSelectedDoc} />
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
            {error}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 pb-10">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {Array.from({ length: 18 }).map((_, i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                <div className="h-[160px] bg-gray-100 animate-pulse" />
                <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/60">
                  <div className="h-3 bg-gray-200 animate-pulse rounded w-2/3 mx-auto" />
                </div>
              </div>
            ))}
          </div>
        ) : molecules.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Atom size={40} className="text-gray-300 mb-3" />
            <p className="font-medium text-gray-600 mb-1">
              {selectedDoc
                ? `No molecules found in "${selectedDoc.name}"`
                : "No molecules yet"}
            </p>
            <p className="text-sm text-gray-400">
              {selectedDoc
                ? "Try a different document"
                : activeTab !== "all" && activeTab !== "personal"
                ? "No molecules found in this organisation's documents"
                : "Upload documents containing chemical structures to get started"}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {molecules.map((mol, i) => (
                <MoleculeCard key={`${mol.smiles}-${i}`} molecule={mol} />
              ))}
            </div>

            <div ref={sentinelRef} className="mt-8 flex flex-col items-center gap-2 py-4">
              {loadingMore && (
                <>
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-[#1d365d] rounded-full animate-spin" />
                  <p className="text-sm text-gray-400">
                    {molecules.length} of {total} molecules
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Molecules;
